import { PERMISSIONS } from "@procuremate/shared";
import { LogOut } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useNavigate } from "react-router";
import { useHasPermission } from "@/app/require-permission";
import { useCurrentUser, useLogout, useSaveLocale } from "@/features/auth/api";
import { NotificationBell } from "@/features/notifications/notification-bell";
import { chosenLanguage, type Language, SUPPORTED_LANGUAGES, setLanguage } from "@/i18n";

/** Signed-in layout: navy header with the white TeamSystem wordmark (BRAND.md). */
export function AppShell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const me = useCurrentUser();
  const logout = useLogout();
  const canAdminister = useHasPermission(PERMISSIONS.USERS_READ);
  const canReview = useHasPermission(PERMISSIONS.REQUESTS_REVIEW);
  const { mutate: saveLocale } = useSaveLocale();
  const savedLocale = me.data?.locale;

  // A language picked before signing in (or before it was saved on the account) reaches the account here.
  useEffect(() => {
    const chosen = chosenLanguage();
    if (savedLocale && chosen && chosen !== savedLocale) saveLocale(chosen);
  }, [savedLocale, saveLocale]);

  function changeLanguage(language: Language) {
    setLanguage(language);
    if (savedLocale && language !== savedLocale) saveLocale(language);
  }

  return (
    <div className="min-h-dvh bg-bg">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:rounded-sm focus:bg-surface focus:px-3 focus:py-2"
      >
        {t("app.skipToContent")}
      </a>
      <header className="bg-primary text-on-primary">
        {/* Phones: brand + actions on the first row, navigation on its own scrollable row. */}
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 sm:h-16 sm:flex-nowrap sm:gap-x-6 sm:py-0">
          <img src="/brand/ts-wordmark-white.png" alt="TeamSystem" className="h-5 w-auto sm:h-6" />
          <span aria-hidden="true" className="h-6 w-px bg-on-primary/30" />
          <span className="font-heading text-base font-semibold sm:text-lg">{t("app.name")}</span>
          <nav
            className="order-last flex w-full gap-4 overflow-x-auto text-sm sm:order-none sm:ml-6 sm:w-auto"
            aria-label={t("nav.main")}
          >
            <NavLink
              to="/"
              end
              className={({ isActive }) => (isActive ? "underline underline-offset-4" : "opacity-90")}
            >
              {t("nav.home")}
            </NavLink>
            {canReview ? (
              <NavLink
                to="/queue"
                className={({ isActive }) => (isActive ? "underline underline-offset-4" : "opacity-90")}
              >
                {t("nav.queue")}
              </NavLink>
            ) : null}
            {canAdminister ? (
              <NavLink
                to="/admin"
                className={({ isActive }) => (isActive ? "underline underline-offset-4" : "opacity-90")}
              >
                {t("nav.admin")}
              </NavLink>
            ) : null}
          </nav>
          <div className="ml-auto flex items-center gap-2 text-sm sm:gap-4">
            <NotificationBell enabled={Boolean(me.data)} />
            <label className="flex items-center gap-2">
              <span className="sr-only">{t("nav.language")}</span>
              <select
                id="language"
                className="rounded-sm bg-primary-hover px-2 py-1 text-on-primary"
                value={i18n.language}
                onChange={(event) => changeLanguage(event.target.value as Language)}
              >
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <option key={lang} value={lang}>
                    {lang.toUpperCase()}
                  </option>
                ))}
              </select>
            </label>
            {me.data ? <span className="hidden sm:inline">{me.data.displayName}</span> : null}
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-sm px-2 py-1 hover:bg-primary-hover"
              disabled={logout.isPending}
              onClick={() => logout.mutate(undefined, { onSettled: () => navigate("/login", { replace: true }) })}
            >
              <LogOut aria-hidden="true" className="h-4 w-4" />
              {/* Icon-only on phones; the text stays as the accessible name. */}
              <span className="sr-only sm:not-sr-only">{t("auth.signOut")}</span>
            </button>
          </div>
        </div>
      </header>
      <main id="main" className="mx-auto max-w-7xl px-4 py-8">
        <Outlet />
      </main>
    </div>
  );
}
